import { Popover } from '@headlessui/react';
import CommentModal from 'components/desktop/shared/temp/post-card/comment-modal';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconDotsHorizontal,
  IconHeart,
  IconHeartOutline,
  IconLocationFill,
  IconPanHorizontal,
  IconPanVertical,
  IconPercentage,
  IconShareVariantOutline,
  IconStairs,
  IconTag,
  IconVectorSquareClose,
} from 'components/icons';
import { IconContactChat } from 'components/icons/IconContactChat';
import ModalWrapper from 'components/shared/modal-wrapper';
import { SITE_MAP } from 'globalConstants';
import { DEMAND, Post } from 'graphql/main/mutations/temp/create-post';
import {
  LIKE_OR_UNLIKE,
  LikeOrUnLikeMutationData,
  LikeOrUnLikeMutationVariables,
} from 'graphql/main/mutations/temp/like-or-unlike';
import {
  POST_DETAIL,
  PostDetailData,
  PostDetailVariables,
} from 'graphql/main/queries/temp/post-detail';
import { useFlexLazyQuery, useFlexMutation, useModal, useToast, useTranslation } from 'hooks';
import { includes, isEqual, minBy } from 'lodash';
import moment from 'moment';
import Link from 'next/link';
import { Fragment, MouseEvent, useState } from 'react';
import { convertNumberToVietnameseCurrencyShortString, timeSince } from 'utils';
import MoreAction from '../more-action';

interface Props {
  float?: boolean;
  post?: Post;
  onDelete?: (id: string) => void;
}

const PostCard = ({ float, post, onDelete }: Props) => {
  const { toasts, addToast, removeToast } = useToast();
  const [privatePost, setPrivatePost] = useState(post);
  const priceRange = minBy(
    privatePost?.priceDetail,
    (priceDetailItem) => priceDetailItem.price?.from,
  );
  const translation = useTranslation();
  const { showing, toggle } = useModal();
  const [likeOrUnlike] = useFlexMutation<LikeOrUnLikeMutationData, LikeOrUnLikeMutationVariables>(
    'main',
    LIKE_OR_UNLIKE,
  );
  const [postDetail] = useFlexLazyQuery<PostDetailData, PostDetailVariables>('main', POST_DETAIL);

  const like = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (privatePost) {
      const { errors: likeOrUnlikeErrors } = await likeOrUnlike({
        variables: { postId: privatePost.id },
      });
      if (likeOrUnlikeErrors) {
        addToast('error', likeOrUnlikeErrors[0].message);
      } else {
        await completeUpdating();
      }
    }
  };
  const completeUpdating = async () => {
    if (privatePost) {
      const { data } = await postDetail({
        variables: {
          postId: privatePost?.id,
        },
      });
      const { postDetail: _postDetail } = data ?? {};
      setPrivatePost(_postDetail);
    }
  };
  const comment = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    toggle();
  };
  const share = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Fragment>
      <Link
        href={
          privatePost?.id
            ? `/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.REAL_ESTATE_POST.INDEX}/${privatePost.id}`
            : '#'
        }
        className={`group flex flex-col space-y-[16px] rounded-[16px] bg-paper px-[12px] py-[16px] ${
          float ? 'shadow-4 border border-stroke' : ''
        }`}
      >
        <div className='flex min-h-[36px] items-center justify-between space-x-[24px]'>
          <div className='flex max-w-[60%] items-center space-x-[10px]'>
            <Link
              href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${privatePost?.user?.id}`}
            >
              <div className='h-[36px] w-[36px] cursor-pointer overflow-hidden rounded-full'>
                <img
                  src={privatePost?.user?.avatar?.originalUrl ?? '/images/default-avatar.svg'}
                  alt={privatePost?.user?.fullName}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
              </div>
            </Link>
            <div className='flex flex-col space-y-[4px]'>
              <Link
                href={`/${SITE_MAP.SOCIAL_NETWORK.INDEX}/${SITE_MAP.SOCIAL_NETWORK.SOCIAL_PROFILE}/${privatePost?.user?.id}`}
                className='hover:underline'
              >
                <span className='line-clamp-1 font-[600]'>{privatePost?.user?.fullName}</span>
              </Link>
              <span className='line-clamp-1 text-[12px] leading-[15px] text-text2'>
                {privatePost?.province?.name}
              </span>
            </div>
          </div>
          <div className='relative flex items-center'>
            <span className='mr-[10px] line-clamp-1 text-[12px] leading-[15px] text-text2'>
              {timeSince(moment(privatePost?.createdAt).toDate())}
            </span>
            <Popover className='relative'>
              <Popover.Button type='button'>
                <IconDotsHorizontal className='h-[24px] w-[24px]' />
              </Popover.Button>
              <Popover.Panel unmount={false} className='absolute right-0 top-[35px] z-10'>
                <MoreAction post={privatePost} onComplete={completeUpdating} onDelete={onDelete} />
              </Popover.Panel>
            </Popover>
          </div>
        </div>
        <div>
          <div className='flex flex-col space-y-[16px]'>
            <div className='relative h-[270px] overflow-hidden rounded-[8px]'>
              <img
                src={privatePost?.images?.[0]?.originalUrl ?? '/images/error-image.svg'}
                alt={privatePost?.title}
                loading='lazy'
                className='h-full w-full object-cover transition-all duration-[500ms] ease-in-out group-hover:scale-[1.1]'
              />
              <div className='absolute top-[12px] flex space-x-[8px] font-[600] text-paper'>
                <span
                  className={`rounded-tr-[16px] px-[8px] py-[6px] text-[12px] uppercase leading-[15px] ${
                    includes([DEMAND.sell, DEMAND.needBuy], privatePost?.demand)
                      ? 'bg-accent-sale'
                      : 'bg-accent-rent'
                  }`}
                >
                  {(translation.social.demand as any)[privatePost?.demand as string]}
                </span>
                <span className='rounded-[32px] bg-text px-[8px] py-[4px]'>
                  {privatePost?.realEstateType
                    ? (isEqual(privatePost?.demand, DEMAND.sell)
                        ? translation.social.forSellRealEstateType
                        : (translation.social.forRentRealEstateType as any))[
                        privatePost?.realEstateType as string
                      ]
                    : (translation.social.projectType as any)[privatePost?.projectType as string]}
                </span>
              </div>
            </div>
            <div className='flex items-center space-x-[24px] min-h-[24px]'>
              <div className='flex items-center space-x-[8px]'>
                <IconTag className='h-[20px] min-w-[20px] text-green' />
                <span className='whitespace-nowrap text-[16px] leading-[20px] font-[600] text-alert'>
                  {privatePost?.realEstateType
                    ? privatePost?.price?.from
                      ? convertNumberToVietnameseCurrencyShortString(privatePost.price.from)
                      : '-'
                    : priceRange?.price?.from && priceRange.price.to
                    ? `${convertNumberToVietnameseCurrencyShortString(
                        priceRange.price.from,
                      )} - ${convertNumberToVietnameseCurrencyShortString(priceRange.price.to)}`
                    : priceRange?.price?.from
                    ? convertNumberToVietnameseCurrencyShortString(priceRange.price.from)
                    : priceRange?.price?.to
                    ? convertNumberToVietnameseCurrencyShortString(priceRange.price.to)
                    : '-'}
                </span>
              </div>
              <div className='flex items-center space-x-[8px]'>
                <IconPercentage className='h-[20px] min-w-[20px] text-orange' />
                <div className='line-clamp-1'>
                  {privatePost?.commission ? (
                    <Fragment>
                      <span className='font-[600]'>{privatePost.commission}</span>{' '}
                      <span>Hoa hồng</span>
                    </Fragment>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            </div>
            <div className='h-[1px] bg-stroke' />
            <div className='flex flex-col space-y-[12px]'>
              <span className='line-clamp-2 min-h-[46px] break-words text-[16px] font-[600] capitalize leading-[23px]'>
                {privatePost?.title}
              </span>
              <div className='flex min-h-[16px] items-center space-x-[6px] text-text2'>
                <IconLocationFill className='h-[16px] min-w-[16px] w-[16px]' />
                <span className='line-clamp-1 text-[13px] leading-[16px]'>{`${privatePost?.ward?.name}, ${privatePost?.district?.name}, ${privatePost?.province?.name}`}</span>
              </div>
              <div className='flex min-h-[42px] justify-around space-x-[12px] rounded-[8px] bg-secondary px-[12px] py-[8px]'>
                <div className='flex w-[calc((100%-36px)/4)] items-center justify-center space-x-[6px] rounded-[6px] bg-paper px-[8px] py-[4px]'>
                  <IconPanHorizontal className='h-[16px] min-w-[16px] w-[16px] text-primary' />
                  <span className='truncate'>
                    {privatePost?.width?.from ? privatePost.width.from.toLocaleString() : '-'}
                  </span>
                </div>
                <div className='flex w-[calc((100%-36px)/4)] items-center justify-center space-x-[6px] rounded-[6px] bg-paper px-[8px] py-[4px]'>
                  <IconPanVertical className='h-[16px] min-w-[16px] w-[16px] text-primary' />
                  <span className='truncate'>
                    {privatePost?.length?.from ? privatePost.length.from.toLocaleString() : '-'}
                  </span>
                </div>
                <div className='flex w-[calc((100%-36px)/4)] items-center justify-center space-x-[6px] rounded-[6px] bg-paper px-[8px] py-[4px]'>
                  <IconVectorSquareClose className='h-[16px] min-w-[16px] w-[16px] text-primary' />
                  <span className='truncate'>
                    {privatePost?.area?.from
                      ? `${privatePost.area.from.toLocaleString()} m\u00B2`
                      : '-'}
                  </span>
                </div>
                <div className='flex w-[calc((100%-36px)/4)] items-center justify-center space-x-[6px] rounded-[6px] bg-paper px-[8px] py-[4px]'>
                  <IconStairs className='h-[16px] min-w-[16px] w-[16px] text-primary' />
                  <span className='truncate'>
                    {privatePost?.floorCount?.from
                      ? privatePost.floorCount.from.toLocaleString()
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='flex items-center justify-end space-x-[12px]'>
          <span className='text-[12px] leading-[15px] text-text2 hover:underline'>
            {privatePost?.likeCount} Thích
          </span>
          <button
            type='button'
            className='text-[12px] leading-[15px] text-text2 hover:underline'
            onClick={comment}
          >
            {privatePost?.commentCount} Bình luận
          </button>
        </div>
        <div className='h-[1px] bg-stroke' />
        <div className='flex items-center justify-between space-x-[8px] px-[24px]'>
          <button
            type='button'
            className={`flex items-center space-x-[8px] ${
              privatePost?.isLiked ? 'text-[#ff4f66]' : 'text-text2 hover:text-primary'
            }`}
            onClick={like}
          >
            {privatePost?.isLiked ? (
              <IconHeart className='h-[24px] w-[24px]' />
            ) : (
              <IconHeartOutline className='h-[24px] w-[24px]' />
            )}
            <span className='min-w-[60px]'>{privatePost?.isLiked ? 'Bỏ thích' : 'Yêu thích'}</span>
          </button>
          <button
            type='button'
            className='flex items-center space-x-[8px] text-text2 hover:text-primary'
            onClick={comment}
          >
            <IconContactChat className='h-[24px] w-[24px]' />
            <span className='min-w-[60px]'>Bình luận</span>
          </button>
          <button
            type='button'
            className='flex items-center space-x-[8px] text-text2 hover:text-primary'
            onClick={share}
          >
            <IconShareVariantOutline className='h-[20px] w-[20px]' />
            <span className='min-w-[60px]'>Chia sẻ</span>
          </button>
        </div>
      </Link>
      <ModalWrapper showing={showing} onToggle={toggle}>
        <CommentModal
          post={privatePost}
          onLike={like}
          onCompleteCommenting={completeUpdating}
          onClose={toggle}
        />
      </ModalWrapper>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default PostCard;

export const PostCardSkeleton = () => (
  <div className='h-[644px] rounded-[16px] bg-tertiary animate-pulse' />
);
